import { useMutation } from 'react-query';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import { changeAppLanguage, changePassword, getUserConfirm, login } from '../api/auth';

import { useCurrentUserProfile } from './user';
import i18n from '../i18n';
import { SUPER_ADMIN_DOMAIN } from '../utils/constant';
import routes from '../routes';
import { consumePostLoginPath } from '../utils/postLoginRedirect';

export const useLogin = () => {
  const navigate = useNavigate();
  const { refetch } = useCurrentUserProfile();
  const { mutate, isLoading, error } = useMutation(['user'], (userInfo) => login(userInfo), {
    onSuccess: (data) => {
      i18n.changeLanguage(data?.user?.app_language);
      localStorage.setItem('language', data?.user?.app_language);
      navigate(consumePostLoginPath(), { replace: true });
      refetch();
      toast.success('Login successfully.', {
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
    onError: () => {
      // Show an error toast
      toast.error('Invalid credentials.', {
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    },
  });

  return { mutate, isLoading, error };
};

export const useAppLanguage = () => {
  const { mutate, isLoading, error } = useMutation(changeAppLanguage, {
    onSuccess: () => {
      toast.success(t(`Dashboards.LanguageChanged`));
    },
    onError: () => {
      toast.error('Failed to change language.');
    },
  });

  return { mutate, isLoading, error };
};

export const useChangePassword = () => {
  const navigate = useNavigate();

  const { mutate, isLoading, error } = useMutation(
    ['password'],
    (userInfo) => changePassword(userInfo),
    {
      onSuccess: () => {
        // Show a success toast
        toast.success('Password change successfully.', {
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        navigate(routes.ProfilePage, { replace: true });
      },
      onError: () => {
        // Show an error toast
        toast.error('Old password does not matched.', {
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
    }
  );

  return { mutate, isLoading, error };
};

export const useUserConfirm = () => {
  const navigate = useNavigate();

  const { mutate, isLoading, error } = useMutation(
    ['user-confirm'],
    (userInfo) => getUserConfirm(userInfo),
    {
      onSuccess: () => {
        navigate(routes.LoginPage, { replace: true });
        // Show an success toast
        toast.success('Password set successfully', {
          // position: "bottom-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
      onError: (confirmError) => {
        const status = confirmError?.response?.status;
        const msg = confirmError?.response?.data?.message || confirmError?.response?.data?.msg;
        if (status === 400) {
          return;
        }
        toast.error(msg || confirmError?.message || 'Something went wrong.', {
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      },
    }
  );

  return { mutate, isLoading, error };
};

export const hasSuperAdminAccess = (domains = []) => domains.includes(SUPER_ADMIN_DOMAIN);
